import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Minus, Send, ImagePlus, ArrowRight, Sparkles, Zap } from "lucide-react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { BRAND } from "@/brand";

const POLL_INTERVAL_MS = 12000;

// New EVA palette — modern dark concierge aesthetic
const C = {
  ink: "#e6eaff",          // Primary text (light on dark)
  inkSoft: "#a5b0d6",      // Muted text
  primary: "#346af8",      // Brand blue
  primary2: "#7ea3ff",     // Lighter blue
  accent: "#ffd166",       // Spark yellow
  bg: "#0b1220",           // Deep navy background
  bgSoft: "#141b30",       // Slightly lighter navy for bubbles
  border: "rgba(126,163,255,0.14)",   // Subtle blue border
  borderStrong: "rgba(126,163,255,0.28)",
  bubbleBot: "rgba(255,255,255,0.04)",
  glass: "rgba(255,255,255,0.06)",
};

const QUICK_ACTIONS = [
  { label: "Get a quote", prompt: "I want a live quote for my gift card", icon: Zap },
  { label: "Check my order", prompt: "Can you help me check the status of my submission?", icon: Sparkles },
  { label: "Talk to a human", prompt: "I want to speak to a real person from your team", icon: ArrowRight },
];


export default function LeoChat() {
  const [open, setOpen] = useState(false);
  const [sessionId, setSessionId] = useState(
    () => localStorage.getItem("gc_chat_session") || null
  );
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [requestingManager, setRequestingManager] = useState(false);
  const bottomRef = useRef(null);
  const fileRef = useRef(null);
  const pollRef = useRef(null);
  const sessionIdRef = useRef(sessionId);
  useEffect(() => {
    sessionIdRef.current = sessionId;
  }, [sessionId]);

  // Start session on first open
  useEffect(() => {
    if (open && !sessionId) {
      let body = {};
      try {
        const stored = localStorage.getItem("gc_customer");
        if (stored) {
          const c = JSON.parse(stored);
          if (c?.email) body.email = c.email;
          if (c?.name) body.name = c.name;
        }
      } catch { /* ignore */ }
      api.post("/chat/start", body).then((r) => {
        setSessionId(r.data.session_id);
        localStorage.setItem("gc_chat_session", r.data.session_id);
      });
    }
  }, [open, sessionId]);

  // Closing via the X ends the session entirely, so the next open starts a
  // brand-new chat. (The minimise "−" button keeps the session to resume later.)
  const closeAndReset = () => {
    setOpen(false);
    setMessages([]);
    setSessionId(null);
    localStorage.removeItem("gc_chat_session");
  };


  // Hydrate history when chat opens
  useEffect(() => {
    if (sessionId && open) {
      api.get(`/chat/${sessionId}/messages`).then((r) => setMessages(r.data));
    }
  }, [sessionId, open]);

  // Poll for proactive admin pushes
  useEffect(() => {
    if (!open || !sessionId) return;
    pollRef.current = setInterval(async () => {
      const last = messages.length ? messages[messages.length - 1]?.timestamp : null;
      try {
        const r = await api.get(`/chat/${sessionId}/poll`, { params: last ? { since: last } : {} });
        if (Array.isArray(r.data) && r.data.length) {
          setMessages((prev) => [...prev, ...r.data]);
        }
      } catch { /* ignore */ }
    }, POLL_INTERVAL_MS);
    return () => clearInterval(pollRef.current);
  }, [open, sessionId, messages]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, sending, uploading]);

  // Global "Talk to human" trigger — historically opened LEO + pinged a
  // manager. Now we route to the dedicated SupportChat widget instead, so
  // the customer sees a clearly different human-only window. We also expose
  // a `gc:close-leo` event so that opening SupportChat can minimise LEO.
  useEffect(() => {
    const onClose = () => setOpen(false);
    window.addEventListener("gc:close-leo", onClose);
    return () => window.removeEventListener("gc:close-leo", onClose);
  }, []);

  const send = async (e) => {
    e?.preventDefault();
    const text = input.trim();
    if (!text || !sessionId || sending) return;
    setInput("");
    setMessages((m) => [
      ...m,
      { role: "user", text, timestamp: new Date().toISOString() },
    ]);
    setSending(true);
    try {
      const r = await api.post("/chat/message", { session_id: sessionId, text });
      setMessages((m) => [
        ...m,
        {
          role: "assistant",
          text: r.data.text,
          escalate: r.data.escalate,
          action: r.data.action,
          timestamp: r.data.timestamp,
        },
      ]);
      // If LEO decided to hand off (customer asked for a human/manager/agent),
      // bounce the conversation into the dedicated SupportChat widget so the
      // user clearly sees they're now in a human queue.
      if (r.data.escalate || r.data.action === "show_human_chat") {
        setTimeout(() => {
          window.dispatchEvent(
            new CustomEvent("gc:open-human-support", {
              detail: { sessionId: sessionIdRef.current },
            })
          );
        }, 400);
      }
    } catch (err) {
      setMessages((m) => [
        ...m,
        {
          role: "assistant",
          text: "I had trouble sending that. Please try again.",
          timestamp: new Date().toISOString(),
        },
      ]);
    } finally {
      setSending(false);
    }
  };

  const onPickImage = () => fileRef.current?.click();

  const uploadImage = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file || !sessionId) return;
    if (!["image/jpeg", "image/png", "image/webp"].includes(file.type)) {
      setMessages((m) => [
        ...m,
        { role: "assistant", text: "Only JPEG, PNG or WEBP images are supported.", timestamp: new Date().toISOString() },
      ]);
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setMessages((m) => [
        ...m,
        { role: "assistant", text: "Image is too large (max 5 MB).", timestamp: new Date().toISOString() },
      ]);
      return;
    }
    setUploading(true);
    setMessages((m) => [
      ...m,
      {
        role: "user",
        text: `📷 ${file.name}`,
        timestamp: new Date().toISOString(),
        local_preview: URL.createObjectURL(file),
      },
    ]);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post(`/chat/upload-image?session_id=${sessionId}`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setMessages((m) => [
        ...m,
        {
          role: "assistant",
          text: r.data.reply,
          prefill: r.data.prefill,
          timestamp: new Date().toISOString(),
        },
      ]);
    } catch (err) {
      setMessages((m) => [
        ...m,
        {
          role: "assistant",
          text: err?.response?.data?.detail || "Couldn't analyze that image. Try again with a clearer photo.",
          timestamp: new Date().toISOString(),
        },
      ]);
    } finally {
      setUploading(false);
    }
  };

  const requestManager = async () => {
    if (!sessionId || requestingManager) return;
    setRequestingManager(true);
    try {
      await api.post(`/chat/${sessionId}/request-manager`);
      // Optimistic system message — the real one comes back from the poll
      setMessages((m) => [
        ...m,
        {
          role: "assistant",
          system: true,
          text: "I've pinged a manager — someone will join you here in a moment.",
          timestamp: new Date().toISOString(),
        },
      ]);
    } catch (err) {
      setMessages((m) => [
        ...m,
        {
          role: "assistant",
          text: "Couldn't reach a manager right now. Please try again in a few seconds.",
          timestamp: new Date().toISOString(),
        },
      ]);
    } finally {
      setRequestingManager(false);
    }
  };

  return (
    <>
      <button
        onClick={() => (open ? closeAndReset() : setOpen(true))}
        data-testid="leo-chat-toggle"
        aria-label="Open chat"
        className="fixed bottom-20 right-4 md:bottom-6 md:right-6 w-14 h-14 md:w-16 md:h-16 rounded-full flex items-center justify-center transition-transform hover:scale-105 active:scale-95 group"
        style={{
          background: `linear-gradient(135deg, ${C.primary} 0%, ${C.primary2} 55%, #a5c1ff 100%)`,
          color: "#fff",
          boxShadow: `0 20px 45px -10px ${C.primary}aa, 0 0 0 3px rgba(255,255,255,0.08), inset 0 1px 0 rgba(255,255,255,0.28)`,
          zIndex: 1200,
        }}
      >
        {/* Pulsing halo */}
        {!open && (
          <span
            aria-hidden="true"
            className="absolute inset-0 rounded-full pointer-events-none"
            style={{
              background: `radial-gradient(circle, ${C.primary2}66 0%, transparent 70%)`,
              animation: "evaPulse 2.4s ease-out infinite",
            }}
          />
        )}
        {/* Live green dot */}
        {!open && (
          <span
            aria-hidden="true"
            className="absolute top-1 right-1 w-3 h-3 rounded-full"
            style={{ background: "#22c55e", boxShadow: "0 0 0 2px #0b1220, 0 0 12px #22c55e" }}
          />
        )}
        {open ? <X className="w-6 h-6 relative z-10" /> : <Sparkles className="w-6 h-6 relative z-10" fill="currentColor" strokeWidth={1.4} />}
      </button>

      {/* Inline styles for pulse animation */}
      <style>{`
        @keyframes evaPulse { 0% { transform: scale(1); opacity: 0.7; } 70% { transform: scale(1.35); opacity: 0; } 100% { transform: scale(1); opacity: 0; } }
        @keyframes evaSpark { 0%, 100% { opacity: 0.5; transform: scale(1); } 50% { opacity: 1; transform: scale(1.15); } }
      `}</style>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.97 }}
            transition={{ duration: 0.24, ease: [0.2, 0.7, 0.2, 1] }}
            data-testid="leo-chat-window"
            className="fixed bottom-[92px] right-3 md:bottom-24 md:right-6 w-[calc(100%-24px)] md:w-[400px] h-[600px] max-h-[calc(100vh-230px)] flex flex-col overflow-hidden"
            style={{
              background: C.bg,
              backgroundImage: `radial-gradient(circle at 20% 0%, ${C.primary}22 0%, transparent 45%), radial-gradient(circle at 100% 100%, #ffd16611 0%, transparent 50%)`,
              borderRadius: 22,
              boxShadow: `0 40px 80px -20px rgba(0,0,0,0.6), 0 0 0 1px ${C.borderStrong}`,
              zIndex: 1200,
            }}
          >
            {/* Header — glass gradient with animated sparkle */}
            <div
              className="px-5 pt-4 pb-4 flex items-center gap-3 relative"
              style={{
                background: `linear-gradient(135deg, ${C.primary} 0%, ${C.primary2} 100%)`,
                color: "#fff",
                borderBottom: `1px solid ${C.borderStrong}`,
              }}
            >
              {/* Faint noise overlay */}
              <div aria-hidden="true" className="absolute inset-0 opacity-30 pointer-events-none" style={{ background: "radial-gradient(circle at 30% 30%, rgba(255,255,255,0.15) 0%, transparent 40%)" }} />
              <div
                className="w-11 h-11 rounded-2xl flex items-center justify-center relative"
                style={{ background: "rgba(255,255,255,0.16)", border: "1px solid rgba(255,255,255,0.30)", backdropFilter: "blur(8px)" }}
              >
                <Sparkles className="w-5 h-5" fill="#ffd166" strokeWidth={1.6} style={{ color: "#ffd166", animation: "evaSpark 2.6s ease-in-out infinite" }} />
              </div>
              <div className="flex-1 min-w-0 relative">
                <div className="font-extrabold leading-tight tracking-tight" style={{ fontSize: 17, letterSpacing: "-0.01em" }}>
                  {BRAND.assistantName}
                  <span className="ml-2 inline-flex items-center gap-1 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-widest rounded" style={{ background: "rgba(255,209,102,0.24)", color: "#ffd166", border: "1px solid rgba(255,209,102,0.36)" }}>AI</span>
                </div>
                <div className="inline-flex items-center gap-1.5 mt-0.5" style={{ fontSize: 11.5, color: "rgba(255,255,255,0.86)" }}>
                  <span className="w-1.5 h-1.5 rounded-full" style={{ background: "#4ade80", boxShadow: "0 0 0 3px rgba(74,222,128,0.3)" }} />
                  Always here · avg reply &lt; 30s
                </div>
              </div>
              <button type="button" onClick={() => setOpen(false)} aria-label="Minimise chat" data-testid="leo-chat-minimise" className="h-8 w-8 rounded-lg flex items-center justify-center transition-colors relative" style={{ color: "#fff" }} onMouseEnter={(e) => (e.currentTarget.style.background = "rgba(255,255,255,0.16)")} onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}>
                <Minus className="w-4 h-4" />
              </button>
              <button type="button" onClick={closeAndReset} aria-label="End chat" data-testid="leo-chat-close" className="h-8 w-8 -mr-1 rounded-lg flex items-center justify-center transition-colors relative" style={{ color: "#fff" }} onMouseEnter={(e) => (e.currentTarget.style.background = "rgba(255,255,255,0.16)")} onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}>
                <X className="w-4 h-4" />
              </button>
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto px-4 py-4 space-y-3" style={{ background: "transparent" }}>
              {messages.map((m, i) => {
                const isManager = m.role === "manager";
                const isCustomer = m.role === "user";
                const bubbleStyle = isCustomer
                  ? { background: `linear-gradient(135deg, ${C.primary} 0%, ${C.primary2} 100%)`, color: "#fff", borderBottomRightRadius: 6, boxShadow: `0 8px 24px -12px ${C.primary}88` }
                  : isManager
                  ? { background: "rgba(255,209,102,0.10)", color: C.ink, border: "1px solid rgba(255,209,102,0.32)", borderBottomLeftRadius: 6 }
                  : { background: C.bubbleBot, color: C.ink, border: `1px solid ${C.border}`, borderBottomLeftRadius: 6, backdropFilter: "blur(8px)" };
                return (
                  <div key={i} className={`flex ${isCustomer ? "justify-end" : "justify-start"}`}>
                    <div
                      data-testid={`chat-bubble-${m.role}-${i}`}
                      className="max-w-[82%] rounded-2xl px-4 py-2.5 text-[13.5px] leading-relaxed whitespace-pre-wrap"
                      style={bubbleStyle}
                    >
                      {m.local_preview && (
                        <img src={m.local_preview} alt="upload" className="mb-2 rounded-xl max-w-full max-h-44 object-cover" />
                      )}
                      {m.text}
                      {m.prefill?.brand_slug && (
                        <Link
                          to={`/submit?brand=${encodeURIComponent(m.prefill.brand_slug)}${m.prefill.balance ? `&balance=${m.prefill.balance}` : ""}`}
                          onClick={() => setOpen(false)}
                          data-testid={`chat-prefill-cta-${i}`}
                          className="mt-3 inline-flex items-center gap-2 px-4 h-10 rounded-full text-sm font-semibold no-underline transition-transform hover:scale-[1.02]"
                          style={{ background: "#fff", color: C.primary, boxShadow: `0 6px 20px -8px ${C.primary}88` }}
                        >
                          <Zap className="w-3.5 h-3.5" fill={C.primary} /> Start submission
                          {m.prefill.brand_name ? ` · ${m.prefill.brand_name}` : ""}
                          {m.prefill.balance ? ` · $${m.prefill.balance}` : ""}
                        </Link>
                      )}
                      {m.pushed && (
                        <div data-testid={`chat-push-${i}`} className="mt-2 text-[10px] uppercase tracking-wider font-semibold" style={{ color: isCustomer ? "rgba(255,255,255,0.85)" : C.accent }}>
                          · live update
                        </div>
                      )}
                      {m.escalate && (
                        <div data-testid={`chat-escalation-${i}`} className="mt-2 text-[11px] font-semibold inline-flex items-center gap-1" style={{ color: C.accent }}>
                          ↑ Handed off to {BRAND.managerName}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
              {(sending || uploading) && (
                <div className="flex justify-start">
                  <div className="rounded-2xl px-4 py-3 flex items-center gap-1.5" style={{ background: C.bubbleBot, border: `1px solid ${C.border}`, borderBottomLeftRadius: 6, backdropFilter: "blur(8px)" }}>
                    <span className="typing-dot" style={{ background: C.primary2 }} />
                    <span className="typing-dot" style={{ background: C.primary2 }} />
                    <span className="typing-dot" style={{ background: C.primary2 }} />
                  </div>
                </div>
              )}

              {/* Quick actions — only show when it's a fresh conversation (just greeting) */}
              {messages.length === 1 && !sending && (
                <motion.div
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.15 }}
                  className="pt-1 flex flex-wrap gap-2"
                >
                  {QUICK_ACTIONS.map((qa) => {
                    const Icon = qa.icon;
                    return (
                      <button
                        key={qa.label}
                        type="button"
                        data-testid={`chat-quick-${qa.label.toLowerCase().replace(/\s+/g, "-")}`}
                        onClick={() => { setInput(qa.prompt); }}
                        className="inline-flex items-center gap-1.5 px-3 h-8 rounded-full text-[12px] font-semibold transition-all hover:scale-[1.03]"
                        style={{ background: C.glass, color: C.ink, border: `1px solid ${C.borderStrong}` }}
                        onMouseEnter={(e) => { e.currentTarget.style.background = "rgba(126,163,255,0.16)"; e.currentTarget.style.borderColor = C.primary2; }}
                        onMouseLeave={(e) => { e.currentTarget.style.background = C.glass; e.currentTarget.style.borderColor = C.borderStrong; }}
                      >
                        <Icon className="w-3 h-3" style={{ color: C.accent }} />
                        {qa.label}
                      </button>
                    );
                  })}
                </motion.div>
              )}

              <div ref={bottomRef} />
            </div>

            {/* Composer — pill container with integrated icons */}
            <form onSubmit={send} className="p-3" style={{ background: "rgba(0,0,0,0.25)", borderTop: `1px solid ${C.border}`, backdropFilter: "blur(12px)" }}>
              <div className="flex items-center gap-2 rounded-full pl-2 pr-1 py-1" style={{ background: C.bgSoft, border: `1px solid ${C.borderStrong}` }}>
                <input
                  ref={fileRef}
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  onChange={uploadImage}
                  className="hidden"
                  data-testid="leo-chat-file"
                />
                <button
                  type="button"
                  onClick={onPickImage}
                  disabled={uploading}
                  aria-label="Upload card photo"
                  data-testid="leo-chat-image"
                  className="h-9 w-9 rounded-full flex items-center justify-center disabled:opacity-50 transition-colors flex-shrink-0"
                  style={{ color: C.inkSoft, background: "transparent" }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = "rgba(126,163,255,0.14)"; e.currentTarget.style.color = C.primary2; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; e.currentTarget.style.color = C.inkSoft; }}
                >
                  <ImagePlus className="w-4 h-4" />
                </button>
                <input
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  placeholder={`Ask ${BRAND.assistantName} anything…`}
                  data-testid="leo-chat-input"
                  className="flex-1 h-9 px-1 text-[13.5px] focus:outline-none bg-transparent"
                  style={{ color: C.ink }}
                />
                <button
                  type="submit"
                  disabled={sending || uploading || !input.trim()}
                  data-testid="leo-chat-send"
                  className="h-9 w-9 rounded-full flex items-center justify-center disabled:opacity-40 transition-all hover:scale-105 flex-shrink-0"
                  style={{ background: `linear-gradient(135deg, ${C.primary} 0%, ${C.primary2} 100%)`, color: "#fff", boxShadow: `0 6px 18px -8px ${C.primary}` }}
                >
                  <Send className="w-4 h-4" />
                </button>
              </div>
              <div className="text-center mt-2 text-[10px]" style={{ color: C.inkSoft, letterSpacing: "0.05em" }}>
                <Sparkles className="w-2.5 h-2.5 inline-block mr-1" style={{ color: C.accent }} fill={C.accent} />
                Powered by AI · a human joins on request
              </div>
            </form>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
